import React, { memo, useCallback, useEffect, useState } from 'react'
import { DatePicker, Form, Input, Modal, Select, Spin, message } from 'antd'
import dayjs, { type Dayjs } from 'dayjs'
import fetchAPI from '../../../../../../lib/utils/fetch-api'
import type { ActivityInfo, HrEmployeeEventType } from '../../../../../components/v2/activity-timeline'

type EnumOption = { id: number | string; value: string }

/** Ant Select сравнивает value строго: приводим id из JSON и из API к number */
function coerceSelectId(v: unknown): number | undefined {
  if (v === null || v === undefined || v === '') return undefined
  const n = typeof v === 'number' ? v : Number.parseInt(String(v).trim(), 10)
  return Number.isFinite(n) ? n : undefined
}

function enumSelectOptions(list: EnumOption[] | undefined) {
  const out: { value: number; label: string }[] = []
  for (const e of list || []) {
    const id = coerceSelectId(e.id)
    if (id === undefined) continue
    out.push({ value: id, label: String(e.value ?? '') })
  }
  return out
}

interface EditHrEmployeeEventModalProps {
  open: boolean
  employeeId: string | number
  initial: ActivityInfo | null
  onClose: () => void
  onSaved: () => void
}

const positionRelated: HrEmployeeEventType[] = ['employment', 'transfer', 'position_change']

export const EditHrEmployeeEventModal: React.FC<EditHrEmployeeEventModalProps> = memo(
  ({ open, employeeId, initial, onClose, onSaved }) => {
    const [form] = Form.useForm()
    const [loadingEnums, setLoadingEnums] = useState(false)
    const [enums, setEnums] = useState<{
      hr_post?: EnumOption[]
      hr_department?: EnumOption[]
      office?: EnumOption[]
      hr_employee_dismissal_reason?: EnumOption[]
    }>({})
    const [submitting, setSubmitting] = useState(false)

    useEffect(() => {
      if (!open) return
      const load = async () => {
        setLoadingEnums(true)
        try {
          const res = await fetchAPI('/api/hr/employees/enum')
          if (res?.enum) {
            setEnums(res.enum)
          }
        } catch (e) {
          console.error(e)
          message.error('Не удалось загрузить справочники')
        } finally {
          setLoadingEnums(false)
        }
      }
      load()
    }, [open, message])

    useEffect(() => {
      if (!open || !initial?.id) return
      const nv = (initial.new_value || {}) as Record<string, unknown>
      form.setFieldsValue({
        event_date: initial.event_date ? dayjs(initial.event_date as string) : undefined,
        comment: initial.comment ?? '',
        reason_id: coerceSelectId(initial.reason_id),
        post_id: coerceSelectId(nv.post_id),
        department_id: coerceSelectId(nv.department_id),
        office_id: coerceSelectId(nv.office_id),
      })
    }, [open, initial, form])

    const handleOk = useCallback(async () => {
      if (!initial?.id) return
      try {
        const values = await form.validateFields()
        setSubmitting(true)

        const body: Record<string, unknown> = {
          event_date: (values.event_date as Dayjs | undefined)?.toISOString(),
          comment: values.comment === '' || values.comment === undefined ? null : values.comment,
        }

        if (initial.event_type === 'dismissal' && values.reason_id != null) {
          body.reason_id = coerceSelectId(values.reason_id)
        }

        if (initial.event_type && positionRelated.includes(initial.event_type)) {
          body.new_value = {
            ...(typeof initial.new_value === 'object' && initial.new_value ? initial.new_value : {}),
            post_id: coerceSelectId(values.post_id) ?? null,
            department_id: coerceSelectId(values.department_id) ?? null,
            office_id: coerceSelectId(values.office_id) ?? null,
          }
        }

        const res = await fetchAPI(
          `/api/hr/employees/${employeeId}/events/${initial.id}`,
          {
            method: 'PATCH',
            body: JSON.stringify(body),
          },
          true,
        )

        if (res?.errors || res?.error) {
          const errText = Array.isArray(res.errors) ? res.errors.join(', ') : res.error?.message || 'Ошибка'
          message.error(errText)
          return
        }

        message.success('Событие обновлено')
        onSaved()
      } catch (e: any) {
        if (e?.errorFields) return
        console.error(e)
        message.error('Не удалось сохранить')
      } finally {
        setSubmitting(false)
      }
    }, [employeeId, form, initial, onSaved])

    const eventType = initial?.event_type

    return (
      <Modal
        title="Правка события"
        open={open}
        onCancel={onClose}
        onOk={handleOk}
        confirmLoading={submitting}
        destroyOnClose
        width={560}
      >
        <Spin spinning={loadingEnums}>
          <Form form={form} layout="vertical">
            <Form.Item
              name="event_date"
              label="Дата события"
              rules={[{ required: true, message: 'Укажите дату' }]}
            >
              <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
            </Form.Item>

            {eventType === 'dismissal' && (
              <Form.Item name="reason_id" label="Причина увольнения">
                <Select
                  allowClear
                  placeholder="Выберите причину"
                  options={enumSelectOptions(enums.hr_employee_dismissal_reason)}
                />
              </Form.Item>
            )}

            {eventType && positionRelated.includes(eventType) && (
              <>
                <Form.Item name="post_id" label="Должность">
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    placeholder="Должность"
                    options={enumSelectOptions(enums.hr_post)}
                  />
                </Form.Item>
                <Form.Item name="department_id" label="Отдел">
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    placeholder="Отдел"
                    options={enumSelectOptions(enums.hr_department)}
                  />
                </Form.Item>
                <Form.Item name="office_id" label="Офис">
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    placeholder="Офис"
                    options={enumSelectOptions(enums.office)}
                  />
                </Form.Item>
              </>
            )}

            <Form.Item name="comment" label="Комментарий">
              <Input.TextArea rows={3} />
            </Form.Item>

            {initial?.updated_at && (
              <div style={{ fontSize: 12, color: '#888', marginTop: 8 }}>
                Последнее изменение: {dayjs(initial.updated_at).format('DD.MM.YYYY HH:mm')}
                {initial.updated_by_name ? ` — ${initial.updated_by_name}` : ''}
              </div>
            )}
          </Form>
        </Spin>
      </Modal>
    )
  },
)

EditHrEmployeeEventModal.displayName = 'EditHrEmployeeEventModal'
